import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/shell/Header'
import { TaskDetail } from '../components/task/TaskDetail'
import { ClientLabel, CompleteToggle } from '../components/task/TaskControls'
import { listTasks, listClients, type Task } from '../lib/api'
import { useSession } from '../hooks/useAuth'
import { roleCanManage, roleCanWrite, roleCanBrowseClients } from '@shared/enums'
import { CheckCircle2, Clock, AlertTriangle, Users } from 'lucide-react'

export function Today() {
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  const canManage = roleCanManage(role)
  const canWrite = roleCanWrite(role)
  const canOpenClients = roleCanBrowseClients(role)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

  const tasks = useQuery({ queryKey: ['tasks'], queryFn: () => listTasks() })
  // Only fetched for the client count, which is account-level — a role without
  // the Clients module has no use for it and shouldn't be asking for it.
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => listClients(),
    enabled: canManage,
  })

  const allTasks = tasks.data?.items ?? []
  const today = new Date().toISOString().slice(0, 10)

  const dueToday = allTasks.filter((t) => t.due === today && t.status !== 'done')
  const overdue = allTasks.filter((t) => t.due && t.due < today && t.status !== 'done')
  const inProgressCount = allTasks.filter((t) => t.status === 'in_progress').length
  const clientCount = clients.data?.items?.length ?? 0

  return (
    <div className="page">
      <Header title="Today" />

      {selectedTaskId && (
        <TaskDetail taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)} />
      )}

      <div className="stat-row">
        <StatCard icon={Clock} label="Due today" value={dueToday.length} />
        <StatCard icon={AlertTriangle} label="Overdue" value={overdue.length} tone="danger" />
        <StatCard icon={CheckCircle2} label="In progress" value={inProgressCount} tone="accent" />
        {canManage && <StatCard icon={Users} label="Active clients" value={clientCount} />}
      </div>

      <section className="section">
        <h2 className="section-title">Tasks due today</h2>
        {dueToday.length === 0 ? (
          <p className="empty-state">Nothing needs you in this window. Clear.</p>
        ) : (
          <TaskLines
            tasks={dueToday}
            canOpenClients={canOpenClients}
            canWrite={canWrite}
            onTaskClick={setSelectedTaskId}
          />
        )}
      </section>

      {overdue.length > 0 && (
        <section className="section">
          <h2 className="section-title" style={{ color: 'var(--state-danger)' }}>
            Overdue
          </h2>
          <TaskLines
            tasks={overdue}
            canOpenClients={canOpenClients}
            canWrite={canWrite}
            onTaskClick={setSelectedTaskId}
            showDue
          />
        </section>
      )}
    </div>
  )
}

/** A compact task line — client name included, and openable like everywhere else. */
function TaskLines({
  tasks,
  canOpenClients,
  canWrite,
  onTaskClick,
  showDue,
}: {
  tasks: Task[]
  canOpenClients: boolean
  canWrite: boolean
  onTaskClick: (taskId: string) => void
  showDue?: boolean
}) {
  return (
    <ul className="task-list-compact">
      {tasks.map((t) => (
        <li
          key={t.id}
          className="task-list-item task-list-item--clickable"
          onClick={() => onTaskClick(t.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') onTaskClick(t.id) }}
        >
          {/* Today is where work actually gets cleared, so it gets the same
              one-click complete the list has. */}
          <CompleteToggle task={t} disabled={!canWrite} size={16} className="task-check-inline" />
          <span className={`priority-dot priority-${t.priority}`} />
          <span className="task-title">{t.title}</span>
          <ClientLabel clientId={t.clientId} clientName={t.clientName} linkTo={canOpenClients} />
          <span className="text-muted">{showDue ? t.due : t.discipline}</span>
        </li>
      ))}
    </ul>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: any
  label: string
  value: number
  tone?: 'accent' | 'danger'
}) {
  return (
    <div className="stat-card">
      <Icon
        size={20}
        className={tone === 'danger' ? 'text-danger' : tone === 'accent' ? 'text-accent' : 'text-muted'}
      />
      <div className="stat-card-content">
        <span className="stat-value mono">{value}</span>
        <span className="stat-label">{label}</span>
      </div>
    </div>
  )
}
